// Shared mood scale used by EchoCard, MoodEmojiRow, and the Echoes dashboard.
// Categorical codes match the CHECK constraint in
// supabase/migrations/20260622_voice_notes_echo.sql. The 2/4/6/8/10 score
// mapping projects the 5-point internal scale onto a 10-point display so that
// the KPI/tooltip numbers read at the same granularity as the mock.
//
// v2 (2026-07-06): Echoes now capture a richer 23-code emotion (see
// utils/emotions.js). Rows carry both `emotion_before/after` (source of truth
// for new captures) and `mood_before/after` (derived valence bucket, kept for
// rollback safety + legacy rows). Consumers should prefer the resolve* helpers
// below, which read emotion first and fall back to the legacy code.

import {
  emotionToLabel,
  emotionToScore,
  emotionToBg,
  emotionToFg,
  isEmotion,
  isEmotionInput,
} from './emotions';

export const MOOD_CODES = ['rough', 'low', 'neutral', 'good', 'great'];

export const MOOD_META = {
  rough:   { emoji: '😞', label: 'Rough',   score: 2 },
  low:     { emoji: '🙁', label: 'Low',     score: 4 },
  neutral: { emoji: '😐', label: 'Neutral', score: 6 },
  good:    { emoji: '🙂', label: 'Good',    score: 8 },
  great:   { emoji: '😄', label: 'Great',   score: 10 },
};

// Null / undefined / unknown collapse to neutral per product decision:
// missing mood should not be treated as "worst" in aggregates or visuals.
const FALLBACK = 'neutral';

const safe = (code) => (MOOD_META[code] ? code : FALLBACK);

export const moodToScore = (code) => MOOD_META[safe(code)].score;
export const moodToEmoji = (code) => MOOD_META[safe(code)].emoji;
export const moodToLabel = (code) => MOOD_META[safe(code)].label;

export function scoreToNearestMood(score) {
  if (score == null || Number.isNaN(score)) return FALLBACK;
  let nearest = MOOD_CODES[0];
  let best = Infinity;
  for (const code of MOOD_CODES) {
    const diff = Math.abs(MOOD_META[code].score - score);
    if (diff < best) {
      best = diff;
      nearest = code;
    }
  }
  return nearest;
}

// Emotion-aware readers for the Echoes dashboard. When a row has an
// emotion_* value, prefer that (finer scale + specific label). Otherwise
// fall back to the legacy 5-code mood scale so pre-v2 rows keep rendering.

/**
 * Is there a real affect value on this side of a note?
 *
 * The single question every dashboard consumer has to ask first. True for one
 * of the 23 scored emotions, or a legacy 5-bucket mood. False for null, for an
 * unknown code, and for 'not_sure' — which is a stored answer but not a
 * measurement.
 *
 * Note it reads MOOD_META directly rather than going through safe(), because
 * safe() coerces anything unknown to 'neutral'. That coercion is right for
 * rendering one legacy row and catastrophic for deciding whether a row has data
 * at all.
 */
export const hasLabel = (emotionCode, moodCode) =>
  isEmotion(emotionCode) || Boolean(MOOD_META[moodCode]);

/**
 * Score for one side, or **null when there is nothing real to score**.
 *
 * Returning null rather than the neutral fallback is the fix for a defect this
 * file used to contain: an unlabelled side scored 6 out of 10, so the chart drew
 * a point in the middle of the scale, the KPI shift averaged it in, and the user
 * was shown a feeling they never reported. Callers must filter on hasLabel()
 * before aggregating; the ones that forget now get a visible null instead of a
 * plausible-looking number.
 */
export const resolveScore = (emotionCode, moodCode) => {
  if (isEmotion(emotionCode)) return emotionToScore(emotionCode);
  if (MOOD_META[moodCode]) return moodToScore(moodCode);
  return null;
};

// Display readers. These DO speak for 'not_sure' — "anxious → Not sure" is a
// legitimate thing to render on a card, even though it never becomes a point.
export const resolveLabel = (emotionCode, moodCode) =>
  (isEmotionInput(emotionCode) ? emotionToLabel(emotionCode) : moodToLabel(moodCode));

export const resolveEmoji = (emotionCode, moodCode) =>
  (isEmotionInput(emotionCode) ? null : moodToEmoji(moodCode));

export const resolveChipBg = (emotionCode, moodCode) => {
  if (isEmotionInput(emotionCode)) return emotionToBg(emotionCode);
  return null;
};

export const resolveChipFg = (emotionCode, moodCode) => {
  if (isEmotionInput(emotionCode)) return emotionToFg(emotionCode);
  return null;
};

// Splits on sentence terminators and returns the last two non-empty sentences
// joined by a space. Preserves the original punctuation on each sentence.
export function lastTwoSentences(text) {
  if (!text || typeof text !== 'string') return '';
  const trimmed = text.trim();
  if (!trimmed) return '';
  const matches = trimmed.match(/[^.!?]+[.!?]+/g);
  if (!matches || matches.length === 0) return trimmed;
  const tail = matches.slice(-2).map((s) => s.trim()).filter(Boolean);
  return tail.join(' ');
}
